//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import { useMemo } from 'react';

import {
  GetSamplingOpportunitiesRequest,
  PurchaseOption,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { useGetSamplingOpportunitiesQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';

interface UsePurchaseOptionsParams {
  isExpiredPurchaseOptionSelected?: boolean;
  productTypes?: GetSamplingOpportunitiesRequest['productTypes'];
  readOnly?: boolean;
  selectedOptions?: PurchaseOption[];
}

interface UsePurchaseOptionsResult {
  error: Error | undefined;
  isFetching: boolean;
  isPending: boolean;
  purchaseOptions: PurchaseOption[];
}

/**
 * Hook that fetches and filters purchase options based on the mode (readonly vs editable).
 * - Filters out options with 0 or negative balance (for both modes)
 * - In readonly mode: filters out all expired options
 * - In editable mode: filters out expired options by default; when `isExpiredPurchaseOptionSelected` is true, expired options are shown only if they are currently selected
 */
export const usePurchaseOptions = ({
  isExpiredPurchaseOptionSelected = false,
  productTypes,
  readOnly = false,
  selectedOptions = [],
}: UsePurchaseOptionsParams): UsePurchaseOptionsResult => {
  const { data, error, isFetching, isPending } =
    useGetSamplingOpportunitiesQuery(productTypes ? { productTypes } : {});

  const purchaseOptions: PurchaseOption[] = useMemo(
    () => data?.opportunities ?? [],
    [data],
  );

  const filteredPurchaseOptions = useMemo(() => {
    return purchaseOptions.filter((option) => {
      // Check if the option is currently selected
      const isSelected = selectedOptions.some(
        (selected) => selected.opportunityId === option.opportunityId,
      );

      // Filter out options with 0 or negative balance, unless it's selected
      // We keep selected options even with 0 balance so we can show proper expiration/invalid messages
      const hasPositiveBalance = (option.balance ?? 0) > 0;
      if (!hasPositiveBalance && !isSelected) {
        return false;
      }

      const isExpired = moment(option.expiryDate).isBefore(moment());

      if (!isExpired) {
        return true; // Always show non-expired options
      }

      if (readOnly) {
        return false; // Never show expired options in readonly mode
      }

      // In editable mode, show expired options only if they are selected
      if (isExpiredPurchaseOptionSelected) {
        return isSelected;
      }

      return false;
    });
  }, [
    isExpiredPurchaseOptionSelected,
    purchaseOptions,
    readOnly,
    selectedOptions,
  ]);

  return {
    error: error ?? undefined,
    isFetching,
    isPending,
    purchaseOptions: filteredPurchaseOptions,
  };
};
